import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import {
  FiDollarSign, FiFileText, FiCheckCircle,
  FiActivity, FiUsers, FiClock,
} from 'react-icons/fi';
import { api } from '@crm/shared';

import DisbursementReport from './reports/tabs/DisbursementReport';
import LoginMIS           from './reports/tabs/LoginMIS';
import SanctionMIS        from './reports/tabs/SanctionMIS';
import ChannelWiseMIS     from './reports/tabs/ChannelWiseMIS';
import BsaWiseMIS         from './reports/tabs/BsaWiseMIS';
import UnderProcessMIS    from './reports/tabs/UnderProcessMIS';

import ReportFilters from './reports/shared/ReportFilters';
import { C, curYear, curMonth } from './reports/shared/tokens';

const TABS = [
  {
    id:'login_mis', label:'Login MIS', icon:<FiFileText size={13}/>,
    color:C.purple, bg:C.purpleL, activeBg:'#6D28D9', shadow:'rgba(109,40,217,0.28)',
    showUser:true, showDateWise:true, showLocation:true,
  },
  {
    id:'sanction_mis', label:'Sanction MIS', icon:<FiCheckCircle size={13}/>,
    color:C.skyB,   bg:C.skyL,    activeBg:'#0369A1', shadow:'rgba(3,105,161,0.28)',
    showUser:true, showDateWise:true, showLocation:true,
  },
  {
    id:'disbursement', label:'Disbursement MIS', icon:<FiDollarSign size={13}/>,
    color:C.green,  bg:C.greenL,  activeBg:'#15803D', shadow:'rgba(21,128,61,0.28)',
    showUser:true, showDateWise:true, showLocation:true,
  },
  {
    id:'channel_wise', label:'Channel Wise', icon:<FiActivity size={13}/>,
    color:C.teal,   bg:C.tealL,   activeBg:C.teal,    shadow:'rgba(13,148,136,0.28)',
    showChannel:true, showDateWise:true, showLocation:true,
  },
  {
    id:'bsa_wise', label:'BSA Wise', icon:<FiUsers size={13}/>,
    color:C.orange, bg:C.orangeL, activeBg:C.orange,  shadow:'rgba(234,88,12,0.28)',
    showBsa:true, showDateWise:true, showLocation:true,
  },
  {
    id:'under_process', label:'Under Process', icon:<FiClock size={13}/>,
    color:C.gold,   bg:C.goldL,   activeBg:C.goldB,   shadow:'rgba(245,166,35,0.28)',
    noFilter: true,
  },
];

export default function Reports() {
  const [activeTab, setActiveTab] = useState('login_mis');

  // ── Shared filter state ─────────────────────────────────────
  const [year,          setYear]          = useState(String(curYear));
  const [month,         setMonth]         = useState(String(curMonth));
  const [selUser,       setSelUser]       = useState('');
  const [dateWise,      setDateWise]      = useState('');
  const [location,      setLocation]      = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [bsaFilter,     setBsaFilter]     = useState('');

  // ── Reference data ──────────────────────────────────────────
  const [users,    setUsers]    = useState([]);
  const [channels, setChannels] = useState([]);
  const [bsas,     setBsas]     = useState([]);

  // ── Load trigger ────────────────────────────────────────────
  const [loading,     setLoading]     = useState(false);
  const [searched,    setSearched]    = useState(false);
  const [loadTrigger, setLoadTrigger] = useState(0);

  useEffect(() => {
    api.getUsers({ page_size: 200 })
      .then(res => setUsers(
        Array.isArray(res?.data?.results) ? res.data.results
        : Array.isArray(res?.data) ? res.data : []
      )).catch(() => {});

    api.getChannels?.({ page_size: 200 })
      .then(res => setChannels(
        Array.isArray(res?.data?.results) ? res.data.results
        : Array.isArray(res?.data) ? res.data : []
      )).catch(() => {});

    api.getBsaList?.({ page_size: 200 })
      .then(res => setBsas(
        Array.isArray(res?.data?.results) ? res.data.results
        : Array.isArray(res?.data) ? res.data : []
      )).catch(() => {});
  }, []);

  const handleLoad = () => { setSearched(true); setLoadTrigger(t => t + 1); };

  const handleReset = () => {
    setSelUser(''); setDateWise(''); setLocation('');
    setChannelFilter(''); setBsaFilter('');
    setSearched(false); setLoadTrigger(0);
  };

  const activeCfg = TABS.find(t => t.id === activeTab);

  const filters = {
    year, month, selUser, dateWise, location,
    channelFilter, bsaFilter,
    users, channels, bsas,
  };

  return (
    <>
      <style>{`
        @keyframes rptShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes rptFadeUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .rpt-anim { animation: rptFadeUp 0.22s ease both; }
      `}</style>

      <Layout pageTitle="Reports">
        <div className="rpt-anim" style={{ display:'flex', flexDirection:'column', gap:14,
          fontFamily:"'Inter',sans-serif", paddingBottom:28 }}>

          {/* Hero */}
          <div style={{ borderRadius:14, overflow:'hidden',
            background:'linear-gradient(120deg,#0D3B5E,#1A5276)',
            boxShadow:'0 4px 20px rgba(0,0,0,0.18)', position:'relative' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
              background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div style={{ padding:'20px 28px' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.48)',
                marginBottom:3, textTransform:'uppercase', letterSpacing:'0.06em' }}>Reports</div>
              <h2 style={{ margin:0, color:'#fff', fontSize:20, fontWeight:800,
                letterSpacing:'-0.03em', lineHeight:1.2 }}>MIS Reports</h2>
              <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.55)', marginTop:4 }}>
                Monthly Information System — Lead pipeline, disbursements, revenue & incentive tracking
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {TABS.map(t => {
              const isAct = activeTab === t.id;
              return (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 18px',
                    border:'none', borderRadius:10, cursor:'pointer', fontFamily:'inherit',
                    fontSize:13, transition:'all 0.15s',
                    background: isAct ? t.activeBg : t.bg,
                    color:      isAct ? '#fff' : t.color,
                    fontWeight: isAct ? 700 : 600,
                    boxShadow:  isAct ? `0 3px 10px ${t.shadow}` : 'none',
                    transform:  isAct ? 'translateY(-1px)' : 'none' }}
                  onMouseEnter={e => { if (!isAct) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 3px 10px ${t.shadow}`; } }}
                  onMouseLeave={e => { if (!isAct) { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'none'; } }}>
                  {t.icon} {t.label}
                </button>
              );
            })}
          </div>

          {/* Shared filter bar — hidden for Under Process */}
          {!activeCfg?.noFilter && (
            <ReportFilters
              year={year} setYear={setYear}
              month={month} setMonth={setMonth}
              selUser={selUser} setSelUser={setSelUser} users={users}
              dateWise={dateWise} setDateWise={setDateWise}
              location={location} setLocation={setLocation}
              channelFilter={channelFilter} setChannelFilter={setChannelFilter} channels={channels}
              bsaFilter={bsaFilter} setBsaFilter={setBsaFilter} bsas={bsas}
              onLoad={handleLoad} loading={loading}
              searched={searched} onReset={handleReset}
              showUser={!!activeCfg?.showUser}
              showDateWise={!!activeCfg?.showDateWise}
              showLocation={!!activeCfg?.showLocation}
              showChannel={!!activeCfg?.showChannel}
              showBsa={!!activeCfg?.showBsa}
            />
          )}

          {/* Tab content */}
          <div key={activeTab}>
            {activeTab === 'disbursement'  && <DisbursementReport filters={filters} loadTrigger={loadTrigger} setLoading={setLoading} searched={searched}/>}
            {activeTab === 'login_mis'     && <LoginMIS           filters={filters} loadTrigger={loadTrigger} setLoading={setLoading} searched={searched}/>}
            {activeTab === 'sanction_mis'  && <SanctionMIS        filters={filters} loadTrigger={loadTrigger} setLoading={setLoading} searched={searched}/>}
            {activeTab === 'channel_wise'  && <ChannelWiseMIS     filters={filters} loadTrigger={loadTrigger} setLoading={setLoading} searched={searched}/>}
            {activeTab === 'bsa_wise'      && <BsaWiseMIS         filters={filters} loadTrigger={loadTrigger} setLoading={setLoading} searched={searched}/>}
            {activeTab === 'under_process' && <UnderProcessMIS filters={filters}/>}
          </div>

        </div>
      </Layout>
    </>
  );
}
